import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Delta, TickerValue } from '../../components/UI';
import { Icon } from '../../components/Icons';
import { useConfirm } from '../../components/Confirm';
import { useBreakpoint } from '../../lib/useBreakpoint';
import { useStockSnapshots } from '../../lib/hooks';
import { getMarketStatus } from '../../lib/marketHours';
import {
  addWatchlistItem,
  createWatchlist,
  deleteWatchlist,
  removeWatchlistItem,
  renameWatchlist,
  reorderWatchlistItems,
  searchSymbols,
  setDefaultWatchlist,
  useWatchlists,
  type SymbolHit,
  type WatchlistDto,
} from '../../lib/dataHooks';

interface Props {
  onPickStock: (sym: string) => void;
}

export const Watchlist = ({ onPickStock }: Props) => {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const wlQ = useWatchlists(30_000);
  const lists = wlQ.data?.watchlists ?? [];

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Track which list the user just renamed/created so the selection follows
  // the optimistic update across refetches even if the API returns lists in a
  // different order.
  const lastTouchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!lists.length) return;
    const stillExists = selectedId && lists.some((l) => l.id === selectedId);
    if (stillExists) return;
    const preferred = lastTouchedRef.current
      ? lists.find((l) => l.id === lastTouchedRef.current)
      : null;
    const fallback = lists.find((l) => l.isDefault) ?? lists[0];
    setSelectedId((preferred ?? fallback).id);
  }, [lists, selectedId]);

  const selected = lists.find((l) => l.id === selectedId) ?? null;

  const handleCreate = async () => {
    const name = window.prompt('Name your new watchlist', 'New list');
    if (!name) return;
    const trimmed = name.trim().slice(0, 80);
    if (!trimmed) return;
    try {
      const r = await createWatchlist(trimmed);
      lastTouchedRef.current = r.watchlist.id;
      setSelectedId(r.watchlist.id);
      await wlQ.refetch();
    } catch {
      // surfaced via the next refetch
    }
  };

  const pagePad = isMobile ? '20px 16px 60px' : '24px 32px 80px';
  const mainCols = isMobile ? '1fr' : '260px 1fr';

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: pagePad }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: '-0.025em',
            }}
          >
            Watchlist
          </h1>
          <div style={{ fontSize: 13, color: 'var(--tk-ink-3)', marginTop: 4 }}>
            Track symbols across multiple lists. Live prices update every 5 seconds.
          </div>
        </div>
      </header>

      {wlQ.loading && lists.length === 0 ? (
        <Card>
          <div style={{ padding: 24, color: 'var(--tk-ink-3)', fontSize: 13 }}>Loading…</div>
        </Card>
      ) : lists.length === 0 ? (
        <Card>
          <div
            style={{
              padding: '40px 24px',
              textAlign: 'center',
              color: 'var(--tk-ink-3)',
              fontSize: 14,
            }}
          >
            <div style={{ marginBottom: 16 }}>You don’t have any watchlists yet.</div>
            <Button icon="plus" onClick={() => void handleCreate()}>
              Create your first list
            </Button>
          </div>
        </Card>
      ) : isMobile ? (
        <div style={{ display: 'grid', gap: 16 }}>
          <MobileListSelector
            lists={lists}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCreate={() => void handleCreate()}
          />
          {selected && (
            <SelectedListPane
              list={selected}
              listCount={lists.length}
              onPickStock={onPickStock}
              onAfterMutate={wlQ.refetch}
              onListDeleted={() => {
                setSelectedId(null);
                lastTouchedRef.current = null;
              }}
              onListRenamed={(id) => {
                lastTouchedRef.current = id;
              }}
            />
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: mainCols, gap: 24 }}>
          <ListsRail
            lists={lists}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCreate={() => void handleCreate()}
          />
          {selected ? (
            <SelectedListPane
              list={selected}
              listCount={lists.length}
              onPickStock={onPickStock}
              onAfterMutate={wlQ.refetch}
              onListDeleted={() => {
                setSelectedId(null);
                lastTouchedRef.current = null;
              }}
              onListRenamed={(id) => {
                lastTouchedRef.current = id;
              }}
            />
          ) : (
            <Card>
              <div style={{ padding: 24, color: 'var(--tk-ink-3)', fontSize: 13 }}>
                Select a list on the left.
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

interface ListsRailProps {
  lists: WatchlistDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
}

const ListsRail = ({ lists, selectedId, onSelect, onCreate }: ListsRailProps) => (
  <Card padding={0}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px 12px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--tk-ink-3)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        Your lists
      </div>
      <button
        type="button"
        onClick={onCreate}
        title="New list"
        aria-label="New watchlist"
        style={{
          width: 26,
          height: 26,
          border: '1px solid var(--tk-line)',
          borderRadius: 999,
          background: 'transparent',
          color: 'var(--tk-ink)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="plus" size={13} stroke={2.4} />
      </button>
    </div>
    <div style={{ borderTop: '1px solid var(--tk-line-2)' }}>
      {lists.map((l) => {
        const isSelected = l.id === selectedId;
        return (
          <button
            key={l.id}
            type="button"
            onClick={() => onSelect(l.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              width: '100%',
              padding: '12px 20px',
              border: 'none',
              borderLeft: isSelected ? '3px solid var(--tk-accent)' : '3px solid transparent',
              background: isSelected ? 'var(--tk-surface-2)' : 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
              color: 'var(--tk-ink)',
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => {
              if (!isSelected) e.currentTarget.style.background = 'var(--tk-surface-2)';
            }}
            onMouseLeave={(e) => {
              if (!isSelected) e.currentTarget.style.background = 'transparent';
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13.5,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {l.name}
                {l.isDefault && (
                  <span
                    title="Default list — used by the dashboard sidebar"
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      color: 'var(--tk-accent)',
                      border: '1px solid var(--tk-accent)',
                      borderRadius: 4,
                      padding: '1px 5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Default
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--tk-ink-3)', marginTop: 2 }}>
                {l.items.length} {l.items.length === 1 ? 'symbol' : 'symbols'}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  </Card>
);

type MobileListSelectorProps = ListsRailProps;

const MobileListSelector = ({ lists, selectedId, onSelect, onCreate }: MobileListSelectorProps) => (
  <Card>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <select
        value={selectedId ?? ''}
        onChange={(e) => onSelect(e.target.value)}
        style={{
          flex: 1,
          padding: '10px 12px',
          fontSize: 14,
          border: '1px solid var(--tk-line)',
          borderRadius: 'var(--tk-radius-sm)',
          background: 'var(--tk-surface)',
          color: 'var(--tk-ink)',
          fontFamily: 'var(--tk-sans)',
        }}
      >
        {lists.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name} ({l.items.length}){l.isDefault ? ' · default' : ''}
          </option>
        ))}
      </select>
      <Button variant="ghost" size="sm" icon="plus" onClick={onCreate}>
        New
      </Button>
    </div>
  </Card>
);

interface SelectedListPaneProps {
  list: WatchlistDto;
  listCount: number;
  onPickStock: (sym: string) => void;
  onAfterMutate: () => Promise<void>;
  onListDeleted: () => void;
  onListRenamed: (id: string) => void;
}

const SelectedListPane = ({
  list,
  listCount,
  onPickStock,
  onAfterMutate,
  onListDeleted,
  onListRenamed,
}: SelectedListPaneProps) => {
  const { ask, dialog } = useConfirm();
  // Local symbol order — lets us reorder optimistically before the API round-trip.
  // Reset whenever the server-side list changes (different id or different items).
  const [localOrder, setLocalOrder] = useState<string[]>(list.items);
  const lastSyncedKeyRef = useRef<string>('');
  useEffect(() => {
    const key = `${list.id}::${list.items.join(',')}`;
    if (key === lastSyncedKeyRef.current) return;
    lastSyncedKeyRef.current = key;
    setLocalOrder(list.items);
  }, [list.id, list.items]);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(list.name);
  useEffect(() => {
    setEditValue(list.name);
    setEditing(false);
  }, [list.id, list.name]);

  const snapshots = useStockSnapshots(localOrder, 5000);
  const marketOpen = getMarketStatus() === 'open';
  const showLive = marketOpen && snapshots.size > 0;

  const handleAdd = async (sym: string) => {
    try {
      await addWatchlistItem(list.id, sym);
      await onAfterMutate();
    } catch {
      // ignore; refetch above will reconcile on next interval
    }
  };

  const handleRemove = async (sym: string) => {
    setLocalOrder((arr) => arr.filter((s) => s !== sym));
    try {
      await removeWatchlistItem(list.id, sym);
      await onAfterMutate();
    } catch {
      await onAfterMutate();
    }
  };

  const handleMove = async (sym: string, dir: -1 | 1) => {
    const idx = localOrder.indexOf(sym);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= localOrder.length) return;
    const next = localOrder.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setLocalOrder(next);
    try {
      await reorderWatchlistItems(list.id, next);
      await onAfterMutate();
    } catch {
      await onAfterMutate();
    }
  };

  const handleRename = async () => {
    const trimmed = editValue.trim().slice(0, 80);
    if (!trimmed || trimmed === list.name) {
      setEditing(false);
      setEditValue(list.name);
      return;
    }
    try {
      await renameWatchlist(list.id, trimmed);
      onListRenamed(list.id);
      await onAfterMutate();
    } catch {
      await onAfterMutate();
    } finally {
      setEditing(false);
    }
  };

  const handleSetDefault = async () => {
    if (list.isDefault) return;
    try {
      await setDefaultWatchlist(list.id);
      await onAfterMutate();
    } catch {
      await onAfterMutate();
    }
  };

  const removeList = async () => {
    try {
      await deleteWatchlist(list.id);
      onListDeleted();
      await onAfterMutate();
    } catch {
      await onAfterMutate();
    }
  };

  const handleDelete = () =>
    ask(
      listCount <= 1
        ? {
            // Nothing to confirm — there is no action available. Said in the
            // app's own dialog rather than a browser alert, for one voice.
            title: 'This is your only watchlist',
            body: <p style={{ margin: 0 }}>Create another one before deleting this list.</p>,
            confirmLabel: 'Close',
            cancelLabel: 'Back',
            tone: 'primary',
          }
        : {
            title: `Delete "${list.name}"?`,
            body: (
              <p style={{ margin: 0 }}>
                The list and its symbols go with it. This cannot be undone.
              </p>
            ),
            confirmLabel: 'Delete',
          },
      () => {
        if (listCount > 1) void removeList();
      },
    );

  return (
    <>
      <Card padding={0}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px 16px',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
            {editing ? (
              <input
                autoFocus
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => void handleRename()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleRename();
                  if (e.key === 'Escape') {
                    setEditing(false);
                    setEditValue(list.name);
                  }
                }}
                maxLength={80}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '6px 10px',
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: '-0.015em',
                  border: '1px solid var(--tk-accent)',
                  borderRadius: 'var(--tk-radius-sm)',
                  background: 'var(--tk-surface)',
                  color: 'var(--tk-ink)',
                  outline: 'none',
                  fontFamily: 'var(--tk-sans)',
                }}
              />
            ) : (
              <h2
                onClick={() => setEditing(true)}
                title="Click to rename"
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: '-0.015em',
                  cursor: 'text',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                }}
              >
                {list.name}
              </h2>
            )}
            {list.isDefault && (
              <span
                title="Default list — used by the dashboard sidebar"
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: 'var(--tk-accent)',
                  border: '1px solid var(--tk-accent)',
                  borderRadius: 4,
                  padding: '2px 6px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                Default
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              title={
                showLive
                  ? 'Live AHL feed'
                  : marketOpen
                    ? 'No data yet'
                    : 'PSX is closed — last snapshot shown'
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                color: showLive ? 'var(--tk-up)' : 'var(--tk-ink-3)',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontFamily: 'var(--tk-mono)',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: showLive ? 'var(--tk-up)' : 'var(--tk-ink-3)',
                  animation: showLive ? 'tk-pulse 1.6s var(--tk-ease) infinite' : undefined,
                }}
              />
              {showLive ? 'Live' : marketOpen ? 'Waiting' : 'Market closed'}
            </span>
            <Button variant="ghost" size="sm" icon="plus" onClick={() => setAdding((v) => !v)}>
              {adding ? 'Close' : 'Add symbol'}
            </Button>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 14,
            padding: '0 24px 14px',
            fontSize: 12,
            color: 'var(--tk-ink-3)',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => void handleSetDefault()}
            disabled={list.isDefault}
            style={{
              border: 'none',
              background: 'transparent',
              color: list.isDefault ? 'var(--tk-ink-3)' : 'var(--tk-ink-2)',
              cursor: list.isDefault ? 'default' : 'pointer',
              padding: 0,
              fontSize: 12,
              fontFamily: 'var(--tk-sans)',
              textDecoration: list.isDefault ? 'none' : 'underline',
              textDecorationStyle: 'dotted',
            }}
          >
            {list.isDefault ? 'Already default' : 'Set as default'}
          </button>
          <span style={{ color: 'var(--tk-line)' }}>·</span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--tk-ink-2)',
              cursor: 'pointer',
              padding: 0,
              fontSize: 12,
              fontFamily: 'var(--tk-sans)',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
            }}
          >
            Rename
          </button>
          <span style={{ color: 'var(--tk-line)' }}>·</span>
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={listCount <= 1}
            title={
              listCount <= 1 ? 'Create another list before deleting this one' : 'Delete this list'
            }
            style={{
              border: 'none',
              background: 'transparent',
              color: listCount <= 1 ? 'var(--tk-ink-3)' : 'var(--tk-down)',
              cursor: listCount <= 1 ? 'not-allowed' : 'pointer',
              padding: 0,
              fontSize: 12,
              fontFamily: 'var(--tk-sans)',
              textDecoration: listCount <= 1 ? 'none' : 'underline',
              textDecorationStyle: 'dotted',
            }}
          >
            Delete list
          </button>
        </div>

        {adding && (
          <AddSymbolPanel
            existing={new Set(localOrder)}
            onPick={async (sym) => {
              await handleAdd(sym);
              setAdding(false);
            }}
            onClose={() => setAdding(false)}
          />
        )}

        {localOrder.length === 0 ? (
          <div
            style={{
              padding: '32px 24px',
              fontSize: 13,
              color: 'var(--tk-ink-3)',
              borderTop: '1px solid var(--tk-line-2)',
              textAlign: 'center',
            }}
          >
            This list is empty — tap <strong style={{ color: 'var(--tk-ink)' }}>Add symbol</strong>{' '}
            above to start tracking.
          </div>
        ) : (
          <ItemsTable
            symbols={localOrder}
            snapshots={snapshots}
            onPickStock={onPickStock}
            onMove={(sym, dir) => void handleMove(sym, dir)}
            onRemove={(sym) => void handleRemove(sym)}
          />
        )}
      </Card>
      {dialog}
    </>
  );
};

interface ItemsTableProps {
  symbols: string[];
  snapshots: ReturnType<typeof useStockSnapshots>;
  onPickStock: (sym: string) => void;
  onMove: (sym: string, dir: -1 | 1) => void;
  onRemove: (sym: string) => void;
}

const ItemsTable = ({ symbols, snapshots, onPickStock, onMove, onRemove }: ItemsTableProps) => {
  const cols = '40px 1fr 110px 100px 110px 32px';
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: cols,
          alignItems: 'center',
          gap: 12,
          padding: '10px 24px',
          borderTop: '1px solid var(--tk-line-2)',
          borderBottom: '1px solid var(--tk-line-2)',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: 'var(--tk-ink-3)',
          textTransform: 'uppercase',
        }}
      >
        <span />
        <span>Symbol</span>
        <span style={{ textAlign: 'right' }}>Last</span>
        <span style={{ textAlign: 'right' }}>Day Δ</span>
        <span style={{ textAlign: 'right' }}>Volume</span>
        <span />
      </div>
      {symbols.map((sym, idx) => {
        const snap = snapshots.get(sym);
        const last = snap?.last;
        const open = snap?.open;
        const change =
          last !== undefined && open !== undefined && open > 0
            ? ((last - open) / open) * 100
            : undefined;
        const volume = snap?.volume;
        const isFirst = idx === 0;
        const isLast = idx === symbols.length - 1;
        return (
          <div
            key={sym}
            style={{
              display: 'grid',
              gridTemplateColumns: cols,
              alignItems: 'center',
              gap: 12,
              padding: '12px 24px',
              borderBottom: '1px solid var(--tk-line-2)',
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--tk-surface-2)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <ReorderButton
                disabled={isFirst}
                title={`Move ${sym} up`}
                onClick={() => onMove(sym, -1)}
                rotate={-90}
              />
              <ReorderButton
                disabled={isLast}
                title={`Move ${sym} down`}
                onClick={() => onMove(sym, 1)}
                rotate={90}
              />
            </div>
            <div onClick={() => onPickStock(sym)} style={{ cursor: 'pointer', minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5, letterSpacing: '0.005em' }}>{sym}</div>
              <div
                style={{
                  fontSize: 11.5,
                  color: 'var(--tk-ink-3)',
                  marginTop: 2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {snap?.source ?? '—'}
              </div>
            </div>
            <div
              className="mono"
              onClick={() => onPickStock(sym)}
              style={{ fontSize: 13.5, fontWeight: 500, textAlign: 'right', cursor: 'pointer' }}
            >
              {last !== undefined ? <TickerValue value={last} decimals={2} /> : '—'}
            </div>
            <div onClick={() => onPickStock(sym)} style={{ textAlign: 'right', cursor: 'pointer' }}>
              {change !== undefined ? (
                <Delta value={change} size="sm" />
              ) : (
                <span style={{ color: 'var(--tk-ink-3)', fontSize: 12 }}>—</span>
              )}
            </div>
            <div
              className="mono"
              onClick={() => onPickStock(sym)}
              style={{
                fontSize: 12.5,
                textAlign: 'right',
                color: 'var(--tk-ink-2)',
                cursor: 'pointer',
              }}
            >
              {volume !== undefined ? volume.toLocaleString('en-PK') : '—'}
            </div>
            <button
              type="button"
              onClick={() => onRemove(sym)}
              title={`Remove ${sym}`}
              aria-label={`Remove ${sym} from watchlist`}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--tk-ink-3)',
                padding: 4,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--tk-down)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--tk-ink-3)')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M6 6l12 12M18 6l-12 12" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
};

interface ReorderButtonProps {
  disabled: boolean;
  title: string;
  onClick: () => void;
  rotate: number;
}

const ReorderButton = ({ disabled, title, onClick, rotate }: ReorderButtonProps) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    title={title}
    aria-label={title}
    style={{
      width: 22,
      height: 16,
      border: 'none',
      background: 'transparent',
      cursor: disabled ? 'not-allowed' : 'pointer',
      color: disabled ? 'var(--tk-line)' : 'var(--tk-ink-3)',
      padding: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    onMouseEnter={(e) => {
      if (!disabled) e.currentTarget.style.color = 'var(--tk-ink)';
    }}
    onMouseLeave={(e) => {
      if (!disabled) e.currentTarget.style.color = 'var(--tk-ink-3)';
    }}
  >
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  </button>
);

interface AddSymbolPanelProps {
  existing: Set<string>;
  onPick: (sym: string) => Promise<void>;
  onClose: () => void;
}

const AddSymbolPanel = ({ existing, onPick, onClose }: AddSymbolPanelProps) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SymbolHit[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const trimmed = q.trim();
    if (!trimmed) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await searchSymbols(trimmed, 10);
        if (!cancelled) setResults(r.results);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  const submit = async (sym: string) => {
    if (existing.has(sym.toUpperCase())) return;
    await onPick(sym.toUpperCase());
  };

  const visibleResults = useMemo(
    () =>
      results
        .filter((r) => !existing.has(r.sym.toUpperCase()))
        .concat(results.filter((r) => existing.has(r.sym.toUpperCase()))),
    [results, existing],
  );

  return (
    <div
      style={{
        borderTop: '1px solid var(--tk-line-2)',
        padding: '14px 24px 16px',
        background: 'var(--tk-surface-2)',
      }}
    >
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          if (e.key === 'Enter') {
            const first = visibleResults.find((r) => !existing.has(r.sym.toUpperCase()));
            if (first) void submit(first.sym);
          }
        }}
        placeholder="Search PSX (e.g. ENGRO, OGDC, MARI)"
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: 13.5,
          border: '1px solid var(--tk-line)',
          borderRadius: 'var(--tk-radius-sm)',
          background: 'var(--tk-surface)',
          color: 'var(--tk-ink)',
          outline: 'none',
          fontFamily: 'var(--tk-sans)',
        }}
      />
      <div style={{ marginTop: 8, maxHeight: 280, overflowY: 'auto' }}>
        {loading && (
          <div style={{ fontSize: 12, color: 'var(--tk-ink-3)', padding: 8 }}>Searching…</div>
        )}
        {!loading && q.trim() && visibleResults.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--tk-ink-3)', padding: 8 }}>No matches.</div>
        )}
        {visibleResults.map((r) => {
          const already = existing.has(r.sym.toUpperCase());
          return (
            <button
              key={r.sym}
              type="button"
              disabled={already}
              onClick={() => void submit(r.sym)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                width: '100%',
                padding: '9px 10px',
                background: 'transparent',
                border: 'none',
                cursor: already ? 'not-allowed' : 'pointer',
                color: already ? 'var(--tk-ink-3)' : 'var(--tk-ink)',
                fontSize: 13,
                textAlign: 'left',
                borderRadius: 6,
                transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => {
                if (!already) e.currentTarget.style.background = 'var(--tk-surface)';
              }}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ minWidth: 0, overflow: 'hidden' }}>
                <strong style={{ fontWeight: 700 }}>{r.sym}</strong>
                <span
                  style={{
                    color: 'var(--tk-ink-3)',
                    marginLeft: 8,
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.name}
                </span>
              </span>
              {already && <span style={{ fontSize: 11, color: 'var(--tk-ink-3)' }}>added</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};
